import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { CrmPaginationComponent } from '../crm-pagination/crm-pagination.component';
import { AddCompanyModalComponent } from './add-company-modal/add-company-modal.component';
import { ViewCompanyModalComponent } from './view-company-modal/view-company-modal.component';
import { AddDealModalComponent } from '../deals/add-deal-modal/add-deal-modal.component';
import * as CrmActions from '../../../state/crm/crm.actions';
import * as CrmSelectors from '../../../state/crm/crm.selectors';
import {MatTooltip} from "@angular/material/tooltip";
import { ReportsSortComponent, SortOption, SortChoice, DEFAULT_SORT } from '../../reports/reports-sort/reports-sort.component';
import { ReportsDateRangeComponent, DateRange, DEFAULT_DATE_RANGE } from '../../reports/reports-date-range/reports-date-range.component';

const COMPANY_SORT_CHOICES: SortChoice[] = [
  { label: 'Date created (newest first)', sort_by: 'created_at',   sort_dir: 'desc' },
  { label: 'Date created (oldest first)', sort_by: 'created_at',   sort_dir: 'asc'  },
  { label: 'Company Name A → Z',          sort_by: 'company_name', sort_dir: 'asc'  },
  { label: 'Company Name Z → A',          sort_by: 'company_name', sort_dir: 'desc' },
];

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, CrmPaginationComponent, AddCompanyModalComponent, ViewCompanyModalComponent, MatTooltip, ReportsSortComponent, ReportsDateRangeComponent, AddDealModalComponent],
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss'
})
export class CompaniesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() searchQuery: string = '';
  @Output() searchQueryChange = new EventEmitter<string>();

  showAddCompanyModal: boolean = false;
  showViewCompanyModal: boolean = false;
  selectedCompany: any = null;
  selectedCompanyId: string | null = null;
  modalMode: 'create' | 'edit' = 'create';

  // "Add deal" flow from a company row
  showAddDealModal: boolean = false;
  preselectedCompanyIdForDeal: number | null = null;

  // Observables from store
  companies$: Observable<any[]>;
  loading$: Observable<boolean>;
  error$: Observable<any>;
  pagination$: Observable<any>;
  contacts$: Observable<any[]>;
  contactsLookup$: Observable<any[]>;
  companiesLookup$: Observable<any[]>;

  // Local state
  paginatedCompanies: any[] = [];
  tableHeight: number = 0;
  pagination: any = {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 10
  };

  currentSort: SortOption = { ...DEFAULT_SORT };
  currentDateRange: DateRange = DEFAULT_DATE_RANGE;
  readonly sortChoices: SortChoice[] = COMPANY_SORT_CHOICES;

  private destroy$ = new Subject<void>();
  private pendingContactId: number | null = null;

  constructor(private store: Store) {
    // Set up observables with selector names
    this.companies$ = this.store.select(CrmSelectors.selectCompanies);
    this.loading$ = this.store.select(CrmSelectors.selectCompaniesLoading);
    this.error$ = this.store.select(CrmSelectors.selectCompaniesError);
    this.pagination$ = this.store.select(CrmSelectors.selectCompaniesPagination);
    this.contacts$ = this.store.select(CrmSelectors.selectContacts);
    this.contactsLookup$ = this.store.select(CrmSelectors.selectContactsLookup);
    this.companiesLookup$ = this.store.select(CrmSelectors.selectCompaniesLookup);
  }

  ngOnInit(): void {
    this.updateTableHeight();

    // Subscribe to pagination changes
    this.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => {
        // console.log('Pagination from store:', pagination);
        this.pagination = pagination || {
          total: 0,
          per_page: 10,
          current_page: 1,
          last_page: 1,
          from: 0,
          to: 0
        };
      });

    // Subscribe to companies changes
    this.companies$
      .pipe(takeUntil(this.destroy$))
      .subscribe(companies => {
        // console.log('Companies from store:', companies);
        // Ensure companies is always an array
        this.paginatedCompanies = Array.isArray(companies) ? companies : [];
        // console.log('PaginatedCompanies:', this.paginatedCompanies);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Search is now triggered manually via button click or Enter key
    // Removed automatic search on searchQuery change to prevent search while typing
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateTableHeight();
  }

  updateTableHeight(): void {
    const headerHeight = 80; // CRM header
    const tabsHeight = 36; // Tabs
    const toolbarHeight = 42; // Period + Sort row (button height + mb-3 spacing)
    const paginationHeight = 36; // Pagination component height
    const padding = 112; // Various paddings

    this.tableHeight = window.innerHeight - headerHeight - tabsHeight - toolbarHeight - paginationHeight - padding;
  }

  onPageChanged(page: number): void {
    this.store.dispatch(CrmActions.loadCompanies(this.buildLoadPayload({ page })));
  }

  onPerPageChanged(perPage: number): void {
    this.store.dispatch(CrmActions.loadCompanies(this.buildLoadPayload({ page: 1, perPage })));
  }

  searchReport(): void {
    this.store.dispatch(CrmActions.loadCompanies(this.buildLoadPayload({ page: 1 })));
  }

  onSortChanged(sort: SortOption): void {
    this.currentSort = sort;
    this.store.dispatch(CrmActions.loadCompanies(this.buildLoadPayload({ page: 1 })));
  }

  onDateRangeChanged(range: DateRange): void {
    this.currentDateRange = range;
    this.store.dispatch(CrmActions.loadCompanies(this.buildLoadPayload({ page: 1 })));
  }

  private buildLoadPayload(overrides: { page?: number; perPage?: number } = {}) {
    return {
      page: overrides.page ?? this.pagination.current_page ?? 1,
      perPage: overrides.perPage ?? this.pagination.per_page ?? 10,
      search: this.searchQuery,
      sortBy: this.currentSort.sort_by,
      sortDir: this.currentSort.sort_dir,
      dateRange: this.currentDateRange,
    };
  }

  viewCompany(company: any): void {
    // console.log('View company:', company);
    this.selectedCompanyId = company.id.toString();
    this.showViewCompanyModal = true;
  }

  editCompany(company: any): void {
    // console.log('Edit company:', company);
    this.selectedCompany = company;
    this.modalMode = 'edit';
    this.showAddCompanyModal = true;
  }

  deleteCompany(company: any): void {
    if (confirm(`Are you sure you want to delete ${company.name}?`)) {
      // console.log('Delete company:', company);
      this.store.dispatch(CrmActions.deleteCompany({ id: company.id }));
    }
  }

  addCompany(): void {
    this.selectedCompany = null;
    this.modalMode = 'create';
    this.showAddCompanyModal = true;
  }

  onCloseCompanyModal(): void {
    this.showAddCompanyModal = false;
  }

  onCloseViewCompanyModal(): void {
    this.showViewCompanyModal = false;
    this.selectedCompanyId = null;
  }

  onDeleteCompanyContact(companyContactId: number): void {
    this.store.dispatch(CrmActions.deleteCompanyContact({ id: companyContactId }));
  }

  onSaveCompany(companyData: any): void {
    // console.log('Company data:', companyData);
    
    // Store the contact ID for later use if it exists
    this.pendingContactId = companyData.contactId || null;

    // Helper function to construct a proper URL
    const formatUrl = (domain: string): string => {
      if (!domain) return '';

      // If it already starts with http:// or https://, return as is
      if (domain.startsWith('http://') || domain.startsWith('https://')) {
        return domain;
      }

      // Otherwise, add https:// prefix
      return `https://${domain}`;
    };

    // Prepare the company data for the API according to the API specification
    const company = {
      name: companyData.companyName,
      url: companyData.domain ? formatUrl(companyData.domain) : undefined,
      phone_number: companyData.phoneNumber,
      linkedin: companyData.linkedin,
      company_identifier: companyData.companyIdentifier,
      industry: companyData.industry,
      number_of_employees: companyData.numberOfEmployees ? parseInt(companyData.numberOfEmployees) : undefined,
      revenue: companyData.revenue ? parseFloat(companyData.revenue) : undefined,
      about_company: companyData.aboutCompany,
      country: companyData.country,
      region: companyData.region,
      city: companyData.city,
      address: companyData.address,
      zip_code: companyData.zipCode
    };

    // console.log('Company to save:', company);

    // Dispatch the appropriate action based on mode
    if (this.modalMode === 'edit' && this.selectedCompany?.id) {
      // Update existing company
      this.store.dispatch(CrmActions.updateCompany({
        id: this.selectedCompany.id,
        company: company
      }));
      
      // In edit mode, if a contact is selected, create company-contact relationship
      if (this.pendingContactId) {
        this.store.dispatch(CrmActions.createCompanyContact({
          company_id: this.selectedCompany.id,
          contact_id: this.pendingContactId,
          is_default: 1
        }));
        
        // Clear the pending contact ID
        this.pendingContactId = null;
      }
    } else {
      // Create new company
      this.store.dispatch(CrmActions.createCompany({ company }));
      
      // Listen for successful company creation to create company-contact relationship
      if (this.pendingContactId) {
        this.store.select(CrmSelectors.selectCompanies)
          .pipe(
            filter(companies => companies && companies.length > 0),
            takeUntil(this.destroy$)
          )
          .subscribe(companies => {
            // Get the newly created company (assuming it's the most recent one)
            const newCompany = companies[0]; // Assuming the most recent company is first
            
            if (newCompany && newCompany.id && this.pendingContactId) {
              // Create company-contact relationship
              this.store.dispatch(CrmActions.createCompanyContact({
                company_id: newCompany.id,
                contact_id: this.pendingContactId,
                is_default: 1
              }));
              
              // Clear the pending contact ID
              this.pendingContactId = null;
            }
          });
      }
    }

    // Close modal immediately
    this.showAddCompanyModal = false;
  }

  getLifecycleStageClass(stage: string): string {
    switch (stage?.toLowerCase()) {
      case 'lead':
        return 'bg-gray-100 text-gray-800';
      case 'opportunity':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      case 'churned':
        return 'bg-red-100 text-red-800';
      case 'prospect':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getContactFullName(contact: any): string {
    if (!contact) return 'Unknown Contact';
    const name = contact.name || '';
    const surname = contact.surname || '';
    return `${name} ${surname}`.trim() || 'Unknown Contact';
  }

  getContactInitials(contact: any): string {
    if (!contact) return '?';
    const name = contact.name || '';
    const surname = contact.surname || '';
    return `${name.charAt(0) || ''}${surname.charAt(0) || ''}`.toUpperCase() || '?';
  }

  getExtraContactsText(extraContacts: any[]): string {
    return extraContacts.map(cc => this.getContactFullName(cc.contact)).join(', ');
  }

  /** Open the Add-Deal modal with this company pre-selected. */
  openAddDealForCompany(company: any, event?: MouseEvent): void {
    event?.stopPropagation();
    this.preselectedCompanyIdForDeal = company?.id ?? null;
    this.showAddDealModal = true;
  }

  onCloseAddDealModal(): void {
    this.showAddDealModal = false;
    this.preselectedCompanyIdForDeal = null;
  }

  onSaveDeal(dealData: any): void {
    const selectedContactId = dealData.contactIds?.[0] ?? null;
    const selectedCompanyId = dealData.companyIds?.[0] ?? null;

    if (!selectedContactId || !selectedCompanyId) {
      alert('Please select both a contact and a company for the deal.');
      return;
    }

    const deal = {
      name: dealData.dealName,
      deal_value: dealData.amount || 0,
      company_id: selectedCompanyId,
      contact_id: selectedContactId,
      icp_score: 0,
      lead_source: 'SalesOMMO',
      user_id: dealData.dealOwner || 1,
      deal_stage: dealData.dealStage || 'early',
      workspace_id: 1,
      close_date: dealData.closeDate,
      type: 'service'
    };

    this.store.dispatch(CrmActions.createDeal({ deal }));
    this.onCloseAddDealModal();
  }
}
